import {
  ResourceCTAButtonContainer,
  ResourcePermissionsDisclaimerWrapper,
} from 'app/components/Resources/ResourceDetail/styled';
import styled from 'styled-components';
import { infiniteRotationAnimation } from 'styles/animations';
import { BREAKPOINTS } from 'styles/media';

export const FileInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FileInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 50px 32px;
  gap: 40px;

  border-radius: 8px;
  border: 1px dashed
    ${({ theme }) => {
      if (theme.isError) return '#EB3D3D';
      if (theme.isSuccess) return '#8DA76C';

      return 'rgba(135, 135, 135, 0.3)';
    }};
  background: ${({ theme }) => {
    if (theme.isError) return 'rgba(255, 163, 102, 0.40)';
    if (theme.isSuccess) return 'rgba(206, 242, 158, 0.40)';

    return '#f7f7f7';
  }};

  cursor: ${({ theme }) => (!theme.isSuccess && !theme.isError ? 'pointer' : 'default')};
`;

interface FileInputButtonProps {
  isAtLimit: boolean;
}

export const FileInputButton = styled.div<FileInputButtonProps>`
  min-height: 48px;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  padding: 16px 48px 16px 40px;

  border-radius: 100px;
  border: 2px solid var(--color-primary);

  opacity: ${({ isAtLimit }) => (isAtLimit ? 0.5 : 1)};

  cursor: pointer;
`;

export const FileInputButtonText = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: normal;
  letter-spacing: 1.4px;
  text-transform: uppercase;
  text-align: center;

  color: var(--color-text);
`;

export const FileInputSubtitle = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: normal;
  text-align: center;

  color: rgba(23, 28, 51, 0.5);
`;

export const FileInputErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FileInputError = styled.span`
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: var(--color-error);
`;

export const FileInputImagesWrapper = styled.div`
  max-width: 100%;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
`;

export const FileInputImagesContainer = styled.div`
  max-width: 542px;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  gap: 14px;
`;

export const FileInputImageActionsContainer = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;

  display: none;
  flex-direction: row;
  gap: 4px;

  ${BREAKPOINTS.tablet} {
    display: flex;
  }
`;

export const FileInputImageContainer = styled.div`
  min-width: 178px;
  min-height: 114px;
  position: relative;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 16px;
  border: 1px solid rgba(141, 167, 108, 0.3);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;

    transition: all 200ms ease-in-out;
  }

  &:hover {
    ${FileInputImageActionsContainer} {
      display: flex;
    }

    & > img {
      transform: scale(1.05);
    }

    &:before {
      background: rgba(0, 0, 0, 0.2);
      backdrop-filter: blur(2px);
    }
  }

  & > div {
    z-index: 2;
  }

  transition: all 200ms ease-in-out;
`;

export const FileInputImagesMainImageContainer = styled(FileInputImageContainer)`
  width: 563px;
  height: 516px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 8px;
  background: var(--light-theme-text-secondary, rgba(23, 28, 51, 0.5));
  box-shadow: 0 6px 24px 0 rgba(0, 0, 0, 0.1);

  ${ResourcePermissionsDisclaimerWrapper} {
    position: absolute;
    top: 23px;
    left: 32px;

    opacity: 0.5;
  }

  ${ResourceCTAButtonContainer} {
    position: absolute;
    top: 36px;
    right: 23px;

    opacity: 0.5;
  }

  ${BREAKPOINTS.tablet} {
    width: 100%;
    height: 100%;

    ${ResourcePermissionsDisclaimerWrapper} {
      display: none;
    }

    ${ResourceCTAButtonContainer} {
      display: none;
    }
  }

  position: relative;
`;

export const FileInputImageActionButton = styled.div`
  width: 40px;
  height: 40px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 100%;

  background: #000000;

  cursor: pointer;
`;

export const FileInputImagesMainImage = styled.img`
  max-width: 348px;
  max-height: 188px;

  object-fit: cover;

  transition: all 200ms ease-in-out;
`;

export const FileInputImagesOtherImage = styled.img`
  max-width: 136px;
  max-height: 73px;

  border-radius: 16px;
  object-fit: cover;

  transition: all 200ms ease-in-out;

  ${BREAKPOINTS.tablet} {
    max-width: 75%;
    max-height: 100%;
  }
`;

export const FileInputNameCloseIconWrapper = styled.div`
  width: 24px;
  height: 24px;

  display: flex;
  align-items: center;
  justify-content: center;

  cursor: pointer;
`;

export const FileInputNameCloseIconContainer = styled.div`
  width: 18px;
  height: 18px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 100%;
  background: rgba(217, 217, 217, 0.5);
`;

// INLINE CTA
export const FileInputInlineCTAWrapper = styled.div`
  height: fit-content;
  max-width: 100%;

  display: flex;
  flex-direction: row;

  border-radius: 32px;
  background: #f7f7f7;

  border: 1px solid #f0f0f0;
  box-shadow: 0px 6px 24px 0px #0000001a;

  cursor: pointer;

  ${BREAKPOINTS.tablet} {
    flex-direction: column;
  }
`;

export const FileInputInlineCTAImageContainer = styled.div`
  width: 332px;
  height: 238px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 32px 0px 0px 32px;

  overflow: hidden;

  ${BREAKPOINTS.tablet} {
    width: 100%;
    height: unset;

    border-radius: 16px 16px 0px 0px;
  }

  background: #d9d9d9;
`;

export const FileInputInlineCTAIconContainer = styled.div`
  width: 106px;
  height: 106px;

  display: flex;
  justify-content: center;
  align-items: center;

  border-radius: 32px;

  ${BREAKPOINTS.tablet} {
    width: 100%;
    height: 100%;

    padding: 16px;

    border-radius: 16px 16px 0px 0px;
  }

  background: var(--color-dark-theme-background-secondary);
`;

export const FileInputInlineCTAIcon = styled.img`
  width: 63px;
  height: 63px;

  object-fit: cover;
`;

export const FileInputInlineCTAImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const FileInputInlineCTAContainer = styled.div`
  max-width: 468px;

  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;

  padding: 0px 24px;

  ${BREAKPOINTS.tablet} {
    width: 100%;

    flex-direction: column;
    gap: 12px;

    padding: 0px 16px;
  }
`;

export const FileInputInlineCTARemoveIconContainer = styled.div`
  width: 18px;
  height: 18px;

  min-width: 18px;
  min-height: 18px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 100%;
  background: rgba(217, 217, 217, 0.5);

  cursor: pointer;
`;

export const FileInputInlineCTATitle = styled.h5`
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.1em;
  text-align: left;

  text-transform: uppercase;

  color: #171c3380;
`;

export const FileInputInlineCTAName = styled.h4`
  font-weight: 400;
  font-size: 24px;
  line-height: 31px;
  letter-spacing: 0em;
  text-align: left;
  word-break: break-all;

  color: var(--color-text);
`;
